import { useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import ExportModalTables from "../../components/ExportModalTables";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import NavigationPanel from "../../components/NavigationPanel";
import CTAs from "../../components/CTAs";
import Toggle from "../../components/Toggle";
import Footer from "../../components/Footer";
import SettingNav from "../../components/settingNav";
import { TableDocument } from "iconsax-reactjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

const SettingsDataManagementPage = () => {
  const navigate = useNavigate();
  const [isExportModalTablesOpen, setExportModalTablesOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);

  const openExportModalTables = useCallback(() => {
    setExportModalTablesOpen(true);
  }, []);

  const closeExportModalTables = useCallback(() => {
    setExportModalTablesOpen(false);
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(false);
  }, []);

  const handleToggle = (state) => {
    console.log("Toggle state is:", state);
  };

  //For table
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const export_history = [
    {
      date: "02/02/2025",
      status: "Success",
      type: "Monthly report",
      link: "Download",
    },
    {
      date: "02/02/2025",
      status: "Failed",
      type: "Custom selection",
      link: "Retry",
    },
    {
      date: "02/02/2025",
      status: "Failed",
      type: "Full data export",
      link: "Retry",
    },
    {
      date: "02/02/2025",
      status: "Pending",
      type: "Full data export",
      link: "See details",
    },
    {
      date: "02/02/2025",
      status: "Success",
      type: "Monthly report",
      link: "Download",
    },
    {
      date: "02/02/2025",
      status: "Pending",
      type: "Custom selection",
      link: "See details",
    },
    {
      date: "02/02/2025",
      status: "Success",
      type: "Monthly report",
      link: "Download",
    },
    {
      date: "02/02/2025",
      status: "Failed",
      type: "Monthly report",
      link: "Retry",
    },
    {
      date: "02/02/2025",
      status: "Success",
      type: "Full data export",
      link: "Download",
    },
    {
      date: "02/02/2025",
      status: "Success",
      type: "Full data export",
      link: "Download",
    },
  ];

  const filteredExportHistory =
    activeTab === "all"
      ? export_history
      : export_history.filter(
          (history) => history.status.toLowerCase() === activeTab.toLowerCase()
        );

  const searchedExportHistory = filteredExportHistory.filter(
    (history) =>
      history.date.toLowerCase().includes(searchQuery.toLowerCase()) ||
      history.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      history.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      history.link.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusColorMap = {
    Failed:
      "bg-semantics-high-high-50 text-semantics-high-high-500 border-semantics-high-high-500",
    Pending:
      "bg-semantics-moderate-moderate-50 text-semantics-moderate-moderate-500 border-semantics-moderate-moderate-500",
    Success:
      "bg-semantics-low-low-50 text-semantics-low-low-500 border-semantics-low-low-500",
  };

  const statusIconMap = {
    Failed: "•",
    Pending: "•",
    Success: "•",
  };

  // Colors for charts
  const COLORS = ["#EF4444", "#F59E0B", "#10B981"]; // Red, Amber, Green

  return (
    <>
      <div className="w-full relative bg-neutrals-neutral-50 min-h-screen overflow-visible text-left text-sm text-neutrals-neutral-300 font-open-sans-ff-body-strong">
        {/* Navigation Panel */}
        <NavigationPanel />

        {/* Main */}
        <div className="w-full absolute top-[136px] flex flex-col items-start justify-start gap-6 px-4 md:px-4 lg:px-24 box-border">
          {/* Heading */}
          <div className="self-stretch bg-white border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-col items-start justify-start pt-0 px-0 pb-6 gap-1.5 text-9xl text-primary-primary-900">
            <b className="self-stretch relative">Settings</b>
            <div className="self-stretch relative text-base text-neutrals-neutral-300">
              Get your billing information, generate/review invoices.
            </div>
          </div>

          <div className="self-stretch flex flex-row items-start justify-start gap-8 text-base">
            {/* Tabs */}
            <SettingNav />

            {/* Right side */}
            <div className="flex-1 flex flex-col items-start justify-start gap-6 text-lg text-neutrals-neutral-900">
              <div className="self-stretch flex flex-col items-start justify-start gap-6">
                <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-start justify-start pt-0 px-0 pb-6">
                  <div className="flex-1 flex flex-col items-start justify-start gap-1.5">
                    <div className="self-stretch relative font-semibold">
                      Data management
                    </div>
                    <div className="self-stretch relative text-sm text-neutrals-neutral-300">
                      Control how your documents, cases, and AI data are stored,
                      exported, and deleted.
                    </div>
                  </div>
                </div>

                <div className="self-stretch flex flex-col items-start gap-8 text-base text-neutrals-neutral-300">
                  <div className="self-stretch grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-center gap-6">
                    {/* Total documents */}
                    <div className="flex-1 rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-col items-start">
                      <div className="self-stretch flex items-start justify-center p-5">
                        <div className="flex-1 flex flex-col items-start gap-6">
                          <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex items-start p-2.5">
                            <TableDocument size={20} />
                          </div>
                          <div className="self-stretch flex flex-col items-start gap-2.5">
                            <div className="self-stretch relative">
                              Total Documents
                            </div>
                            <b className="self-stretch relative text-13xl text-neutrals-neutral-900">
                              1243
                            </b>
                          </div>
                        </div>
                      </div>

                      <div className="self-stretch shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-t-none rounded-b-3xs bg-white border-neutrals-neutral-100 border-solid border-t-[1px] flex flex-col items-end py-4 px-6 text-sm text-neutrals-neutral-900">
                        <CTAs
                          type="Secondarybtn"
                          text="Sync now"
                          borderColor="border-neutrals-neutral-100"
                        />
                      </div>
                    </div>

                    {/* Pie chart */}
                    <div className="h-64 flex-[0.9059] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start p-6 gap-6 text-lg text-neutrals-neutral-900">
                      <div className="self-stretch relative font-semibold">
                        Data categories
                      </div>
                      <div className="self-stretch flex-1 flex items-start gap-3 text-sm">
                        <img
                          className="self-stretch w-[154px] h-[154px]"
                          alt=""
                        />
                        <div className="flex flex-col items-start gap-2.5">
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 relative rounded-num-50 bg-royalblue" />
                            <div className="relative">Legal Docs</div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 relative rounded-num-50 bg-crimson" />
                            <div className="relative">Financial Docs</div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 relative rounded-num-50 bg-goldenrod" />
                            <div className="relative">Communication</div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 relative rounded-num-50 bg-lightgreen" />
                            <div className="relative">Others</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="self-stretch flex flex-col items-start justify-start text-sm">
                  <div className="self-stretch rounded-t-3xs rounded-b-none border-neutrals-neutral-100 border-solid border-t-[1px] border-r-[1px] border-l-[1px] flex flex-row items-center justify-between py-5 px-6 gap-0 text-lg">
                    <div className="flex flex-row items-center justify-start gap-2.5">
                      <div className="relative font-semibold">
                        Export history
                      </div>
                      <div className="rounded-[5px] bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-start py-0.5 px-2 gap-1.5 text-sm">
                        <div className="w-1.5 relative rounded-[50%] bg-primary-primary-500 h-1.5" />
                        <div className="relative font-semibold">10 entries</div>
                      </div>
                    </div>
                    <div className="flex flex-row items-start justify-start gap-2.5 text-sm">
                      <CTAs
                        type="Secondarybtn"
                        text="Export"
                        borderColor="border-neutrals-neutral-100"
                        position="relative"
                        icon={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              stroke="#2d404e"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="1.5"
                              d="M16.44 8.9c3.6.31 5.07 2.16 5.07 6.21v.13c0 4.47-1.79 6.26-6.26 6.26H8.74c-4.47 0-6.26-1.79-6.26-6.26v-.13c0-4.02 1.45-5.87 4.99-6.2M12 15V3.62M15.35 5.85L12 2.5 8.65 5.85"
                            ></path>
                          </svg>
                        }
                        onClick={openExportModalTables}
                      />

                      <CTAs
                        type="Secondarybtn"
                        text="Clear logs"
                        borderColor="border-neutrals-neutral-100"
                        position="relative"
                      />
                    </div>
                  </div>
                  <div className="self-stretch border-neutrals-neutral-100 border-solid border-t-[1px] border-r-[1px] border-l-[1px] flex flex-row items-center justify-between py-3 px-6 gap-0">
                    <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-start">
                      <div className="rounded-tl-3xs rounded-tr-none rounded-br-none rounded-bl-3xs bg-column-bg border-neutrals-neutral-100 border-solid border-r-[1px] flex flex-row items-center justify-center py-2 px-4">
                        <div className="relative font-semibold">View all</div>
                      </div>
                      <div className="border-neutrals-neutral-100 border-solid border-r-[1px] flex flex-row items-center justify-center py-2 px-4">
                        <div className="relative font-semibold">Success</div>
                      </div>
                      <div className="border-neutrals-neutral-100 border-solid border-r-[1px] flex flex-row items-center justify-center py-2 px-4">
                        <div className="relative font-semibold">Pending</div>
                      </div>
                      <div className="rounded-tl-none rounded-tr-3xs rounded-br-3xs rounded-bl-none flex flex-row items-center justify-center py-2 px-4">
                        <div className="relative font-semibold">Failed</div>
                      </div>
                    </div>
                    <div className="flex flex-row items-start justify-start gap-2.5 text-neutrals-neutral-300">
                      <div className="w-[294px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-row items-center justify-start py-2.5 px-3.5">
                        <div className="flex flex-row items-center justify-start gap-2.5">
                          <div className="w-5 relative h-5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2"
                                stroke="#576e7e"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ></path>
                            </svg>
                          </div>
                          <div className="relative">Search</div>
                        </div>
                      </div>
                      <CTAs
                        type="Secondarybtn"
                        text="Filter"
                        borderColor="border-neutrals-neutral-100"
                        icon={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M3 7h18M6 12h12M10 17h4"
                              stroke="#2d404e"
                              stroke-width="1.5"
                              stroke-linecap="round"
                            ></path>
                          </svg>
                        }
                        position="relative"
                      />
                    </div>
                  </div>
                  <div className="self-stretch flex flex-col items-start justify-between gap-0">
                    <div className="self-stretch flex flex-col items-start justify-start">
                      <div className="self-stretch flex flex-row items-start justify-start">
                        <table className="min-w-full divide-y divide-gray-200 border-collapse">
                          <thead className="bg-column-bg">
                            <tr>
                              <th
                                scope="col"
                                className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-l-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                              >
                                Date
                              </th>
                              <th
                                scope="col"
                                className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                              >
                                Status
                              </th>
                              <th
                                scope="col"
                                className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                              >
                                Type
                              </th>
                              <th
                                scope="col"
                                className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-r-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                              >
                                Link
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-neutrals-neutral-50 divide-y divide-gray-200">
                            {searchedExportHistory.map((export_history) => (
                              <tr
                                key={export_history.id}
                                className="hover:bg-gray"
                              >
                                <td className="p-4 items-start border-solid border-b-[1px] border-l-[1px] border-neutrals-neutral-100">
                                  {export_history.date}
                                </td>
                                <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100 whitespace-nowrap">
                                  <span
                                    className={`px-2 py-0.5 text-xs font-semibold rounded-full border-solid border-[1px] border-neutrals-neutral-100 ${
                                      statusColorMap[export_history.status]
                                    }`}
                                  >
                                    <span className="mr-1">
                                      {statusIconMap[export_history.status]}
                                    </span>
                                    {export_history.status}
                                  </span>
                                </td>
                                <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100">
                                  {export_history.type}
                                </td>
                                <td className="p-4 items-start border-solid border-b-[1px] border-r-[1px] border-neutrals-neutral-100">
                                  {export_history.link}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* Pagination */}
                    <div className="self-stretch shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-t-none rounded-b-3xs flex flex-row items-center justify-between pt-3 px-6 pb-4 border-r-[1px] border-solid border-neutrals-neutral-100 border-b-[1px] border-l-[1px]">
                      <CTAs
                        type="Secondarybtn"
                        text="Previous"
                        borderColor="border-neutrals-neutral-100"
                      />
                      <div className="relative">Page 1 of 10</div>
                      <CTAs
                        type="Secondarybtn"
                        text="Next"
                        borderColor="border-neutrals-neutral-100"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <Footer />
            </div>
          </div>
        </div>
      </div>
      {isExportModalTablesOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeExportModalTables}
        >
          <ExportModalTables onClose={closeExportModalTables} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsDataManagementPage;
